import { UseHotkeyOptions, useHotkey } from '@tanstack/react-hotkeys';
import { last } from 'lodash';
import { isPopoverShown } from '@shared/util/popover';
import { useMenuContext } from './MenuContext';

/**
 * Arrow-key navigation for the topmost open menu.
 */
export function MenuKeyboardNavigation() {
  const { isOpen } = useMenuContext();
  if (isOpen) {
    return <RegisterMenuHotkeys />;
  } else {
    return null;
  }
}

const options: UseHotkeyOptions = { conflictBehavior: 'allow' };

function RegisterMenuHotkeys() {
  useHotkey('ArrowDown', focusNextMenuItem, options);
  useHotkey('ArrowUp', focusPreviousMenuItem, options);

  return null;
}

/**
 * Closed menus stay in the DOM — they are popovers, not conditionally rendered —
 * so document order alone would happily pick one that isn't showing. Filtering on
 * `isPopoverShown` narrows it to menus actually on screen, and `last` then takes
 * the innermost of a nested set, which is the one holding focus.
 *
 * `isPopoverShown` rather than a `:popover-open` selector: jsdom throws
 * `SyntaxError: unknown pseudo-class selector` on that pseudo-class, and the
 * helper already swallows it.
 */
function getTopmostOpenMenu(): HTMLElement | undefined {
  const menus = Array.from(document.querySelectorAll<HTMLElement>('[role="menu"]'));
  const shown = menus.filter(isPopoverShown);
  // Fall back to every menu where the popover API is unavailable, so a menu
  // rendered without it stays navigable.
  return last(shown.length > 0 ? shown : menus);
}

// Every row role a menu uses: plain items, and the checkable rows `Menu.Options` renders
const MENU_ITEM_ROLES = ['menuitem', 'menuitemradio', 'menuitemcheckbox'];
const MENU_ITEM_SELECTOR = MENU_ITEM_ROLES.map(role => `[role="${role}"]:not([disabled])`).join();

function getOpenMenuItems(menu: HTMLElement): HTMLElement[] {
  return Array.from(menu.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR));
}

function focusMenuItemBy(offset: number) {
  const menu = getTopmostOpenMenu();
  if (!menu) {
    return;
  }

  const menuItems = getOpenMenuItems(menu);
  if (menuItems.length === 0) {
    return;
  }

  const currentIndex = menuItems.indexOf(document.activeElement as HTMLElement);
  if (currentIndex === -1) {
    // Focus is outside the menu, so enter it from the near end: down starts at
    // the top, up starts at the bottom. Falling through to the modulo below
    // would put ArrowUp on the *second* item, which is what the arithmetic
    // happens to produce for an index of -1.
    menuItems[offset > 0 ? 0 : menuItems.length - 1]?.focus();
    return;
  }

  const nextIndex = (currentIndex + offset + menuItems.length) % menuItems.length;
  menuItems[nextIndex]?.focus();
}

function focusNextMenuItem() {
  focusMenuItemBy(1);
}

function focusPreviousMenuItem() {
  focusMenuItemBy(-1);
}
